import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, User } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO, { articleJsonLd, breadcrumbJsonLd } from '../components/SEO';
import NewsArticleBody from '../components/NewsArticleBody';
import NewsShareButtons from '../components/NewsShareButtons';
import { FileDown } from 'lucide-react';
import { NEWS_AUTHOR, formatNewsDate, getNewsPost, newsPosts } from '../data/news';

const NewsArticlePage = ({ onRequestDemo, onRequestQuote }) => {
  const { slug } = useParams();
  const post = getNewsPost(slug);
  if (!post) return <Navigate to="/news" replace />;

  const url = `https://ctrl.aero/news/${post.slug}`;
  const related = newsPosts.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`${post.title} — ctrl.aero`}
        description={post.metaDescription}
        canonical={url}
        image={`/images/og/news/${post.slug}.png`}
        imageWidth={1200}
        imageHeight={630}
        imageAlt={post.title}
        ogType="article"
        jsonLd={[
          articleJsonLd({
            headline: post.title,
            description: post.excerpt,
            image: `https://ctrl.aero/images/og/news/${post.slug}.png`,
            datePublished: post.date,
            url,
            author: NEWS_AUTHOR,
          }),
          breadcrumbJsonLd([
            { name: 'Home', url: 'https://ctrl.aero/' },
            { name: 'News', url: 'https://ctrl.aero/news' },
            { name: post.title, url },
          ]),
        ]}
      />
      <Header onRequestDemo={onRequestDemo} onRequestQuote={onRequestQuote} />

      <section className="pt-28 pb-10 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <Link to="/news" data-testid="news-back-link" className="inline-flex items-center text-sm text-slate-300 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to News
            </Link>
          </div>
          <span className="inline-block px-3 py-1 bg-blue-500 text-white text-xs font-semibold rounded-full mb-4">
            {post.category}
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6" data-testid="news-article-title">
            {post.title}
          </h1>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-300">
            <span className="inline-flex items-center" data-testid="news-article-date">
              <Calendar className="w-4 h-4 mr-2" /> {formatNewsDate(post.date)}
            </span>
            <span className="inline-flex items-center" data-testid="news-article-author">
              <User className="w-4 h-4 mr-2" /> {NEWS_AUTHOR}
            </span>
          </div>
        </div>
      </section>

      <article className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <figure className="mb-10">
            <div className={`rounded-2xl overflow-hidden border border-slate-100 ${post.imageFit === 'contain' ? 'bg-slate-50 p-8' : ''}`}>
              <img
                src={post.image}
                alt={post.imageAlt || post.title}
                className={`w-full ${post.imageFit === 'contain' ? 'max-h-[420px] object-contain' : 'max-h-[520px] object-cover'}`}
                data-testid="news-article-image"
              />
            </div>
            {post.imageCaption && (
              <figcaption className="mt-3 text-sm text-slate-500">{post.imageCaption}</figcaption>
            )}
          </figure>
          <NewsArticleBody body={post.body} />
          <div className="mt-12 pt-8 border-t border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <NewsShareButtons url={url} title={post.title} />
            {post.pdf && (
              <a
                href={post.pdf}
                download
                data-testid="news-download-pdf"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-[#1e2d3b] text-white text-sm font-semibold hover:bg-blue-700 transition-colors duration-200"
              >
                <FileDown className="w-4 h-4" /> Download Press Release (PDF)
              </a>
            )}
          </div>
        </div>
      </article>

      <section className="py-16 bg-slate-50 border-t border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[#1e2d3b] mb-8">More from CTRL</h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {related.map((p) => (
              <Link
                key={p.slug}
                to={`/news/${p.slug}`}
                data-testid={`news-related-${p.slug}`}
                className="block bg-white rounded-xl border border-slate-200 p-5 hover:border-blue-500 transition-colors duration-300"
              >
                <span className="text-xs text-slate-500">{formatNewsDate(p.date)}</span>
                <h3 className="mt-2 text-base font-semibold text-[#1e2d3b] leading-snug line-clamp-3">{p.title}</h3>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default NewsArticlePage;
